import { IconButton, TableCell } from '@mui/material';
import { SerialBond } from '../debt-service-page.tsx';
import DragAndDropRow from './drag-and-drop-row.tsx';
import { Delete } from '@mui/icons-material';
import moment from 'moment';
import formatter from "../../../tools/formatter.js";
import { DealBuilder } from '../../services/deal-builder.ts';
import useDealBuilderResultStore from '../../../stores/deal-builder-result.store.ts';
import MaturityPicker from './maturity-picker.tsx';
import CurrencyInput from './currency-input.tsx';
import { DebtServiceRow } from '../../services/utils/debt-service.utils.ts';

interface SerialBondRow {
  builder: DealBuilder,
  bond: SerialBond,
  debtServiceRow: DebtServiceRow | undefined,
  showCapitalizedInterest: boolean,
  border: boolean,
}

export default function SerialBondRow({ 
  builder, 
  bond, 
  debtServiceRow,
  showCapitalizedInterest,
  border,
}: SerialBondRow) {
  const { setDealBuilderService } = useDealBuilderResultStore();

  return (
    <DragAndDropRow 
      dragging={false} 
      onDragOver={() => {}}
      indicator="none"
      sx={{
        "& td": {
          ...(!border ? { borderBottom: "none !important" } : {}),
        }
      }}
    >
      <TableCell>
        <MaturityPicker
          format="MM/DD/YYYY"
          value={moment(bond.maturity)}
          firstCouponDate={builder.firstCouponDate}
          onChange={async (value) => {
            if (value.isSame(moment(bond.maturity), "day")) return;
            builder.updateBondDate(bond, value);
            builder.updateDebtService();
            setDealBuilderService(builder);
            await builder.updateProduction(builder.bonds);
            setDealBuilderService(builder);
          }}
        />
      </TableCell>
      <TableCell align="right">
        <CurrencyInput
          className={"switch-db-0"}
          value={bond.principal}
          size="small"
          startAdornment="$"
          locked={bond.lockedPrincipal}
          onLockedChange={(val) => {
            bond.lockedPrincipal = val;
            setDealBuilderService(builder);
          }}
          onValidChange={async (value) => {
            const newPrincipal = value * 1000;
             if (bond.principal === newPrincipal || bond.principal === value) return;
            const diff = newPrincipal - bond.principal;
            builder.totalTargetParAmount += diff;
            bond.principal = newPrincipal;
            bond.lockedPrincipal = true;
            builder.updateDebtService();
            setDealBuilderService(builder);
            await builder.updateProduction(builder.bonds);
            setDealBuilderService(builder);
          }}
          isValueValid={(value) => {
            const valid = (value * 1000) % builder.denomination === 0;
            if(valid) return { valid: true };
            return { valid: false, error: "The principal must be divisible by the denomination." };
          }}
        />
      </TableCell>
      <TableCell align="right">
        <CurrencyInput
          className={"switch-db-1"}
          decimalScale={3}
          size="small"
          value={bond.coupon * 100}
          endAdornment="%"
          locked={bond.lockedCoupon}
          onLockedChange={(val) => {
            bond.lockedCoupon = val;
            setDealBuilderService(builder);
          }}
          onValidChange={async (value) => {
            const diff = bond.coupon - value / 100;
            if (Math.abs(diff) < 0.00001) return;
            bond.coupon = value / 100
            bond.lockedCoupon = true;
            builder.updateDebtService();
            setDealBuilderService(builder);
            await builder.updateProduction(builder.bonds);
            setDealBuilderService(builder);
          }}
          isValueValid={() => ({ valid: true })}
        />
      </TableCell>

      {debtServiceRow ? (
        <>
          <TableCell align="right">{formatter.money(debtServiceRow.accInterest, false)}</TableCell>
          {showCapitalizedInterest && (
            <TableCell align="right">
              ({formatter.money(debtServiceRow.capitalizedInterest, false)})
            </TableCell>
          )}
          <TableCell align="right">{formatter.money(debtServiceRow.debtService, false)}</TableCell>
          <TableCell align="right">{debtServiceRow.annualDS ? formatter.money(debtServiceRow.annualDS, false) : "-"}</TableCell>
        </>
      ) : (
        <>
          <TableCell align="right"></TableCell>
          {showCapitalizedInterest && <TableCell align="right"></TableCell>}
          <TableCell align="right"></TableCell>
          <TableCell align="right"></TableCell>
        </>
      )}

      <TableCell align="center">
        <IconButton 
          size="small"
          sx={{ color: 'error.main', '&:hover': { color: 'error.dark' } }}
          onClick={async () => {
            builder.deleteBond(bond);
            builder.updateDebtService();
            await builder.updateProduction();
            setDealBuilderService(builder);
          }}
        >
          <Delete fontSize="small" />
        </IconButton>
      </TableCell>
    </DragAndDropRow>
  );
};
